/**
 * AuthFooterLink — the centred "¿Ya tenés cuenta? Iniciá sesión" row that
 * closes an auth form.
 *
 * Four screens repeated the same container, the same FadeInUp at
 * STAGGER * 6, the same 14pt type and the same coral link. `prompt` is
 * optional because reset-password closes with the link alone.
 *
 * login / register / phone-login / phone-verify deliberately keep their own
 * footers: they run on different entrance delays and paint the link with
 * `isDark ? coral : borderFocused`. Folding that drift into props here would
 * turn one primitive into a switchboard and repaint four screens.
 */
import { Text, Platform } from 'react-native'
import Animated, { FadeInUp, useReducedMotion } from 'react-native-reanimated'
import { Pressable } from '../ui/Pressable'
import { FONTS, SPACING } from '../../lib/brand'
import { useAuthRhythm } from '../../lib/hooks/use-auth-rhythm'
import { useBreakpoint } from '../../lib/hooks/use-breakpoint'
import { useTheme } from '../../lib/hooks/use-theme'
import { useSemanticColors, DURATION, INTERACTION, STAGGER } from '../../lib/theme/tokens'

const webCursor = Platform.OS === 'web' ? { cursor: 'pointer' as const } : {}

interface AuthFooterLinkProps {
  /** Leading question, e.g. "¿Ya tenés cuenta?". Omit for a lone link. */
  prompt?: string
  linkLabel: string
  onPress: () => void
  accessibilityLabel: string
  testID?: string
  /** Desktop form column stays left-aligned; phone lockups centre. */
  align?: 'center' | 'start'
}

export function AuthFooterLink({
  prompt,
  linkLabel,
  onPress,
  accessibilityLabel,
  testID,
  align = 'center',
}: AuthFooterLinkProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { isDesktop } = useBreakpoint()
  const sp = useAuthRhythm()
  const reducedMotion = useReducedMotion()

  return (
    <Animated.View
      collapsable={false}
      entering={reducedMotion ? undefined : FadeInUp.duration(DURATION.normal).delay(STAGGER * 6)}
      style={{
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: align === 'start' ? 'flex-start' : 'center',
        alignItems: 'center',
        marginTop: sp.altToFooter,
        paddingBottom: isDesktop ? 0 : SPACING.sm,
      }}
    >
      {prompt ? (
        <Text
          style={{
            fontFamily: FONTS.body,
            fontSize: 14,
            color: colors.textSecondary,
          }}
        >
          {prompt}
        </Text>
      ) : null}
      <Pressable
        onPress={onPress}
        // The box IS the target. `hitSlop` used to carry this alone, and
        // react-native-web drops the prop entirely — on the web build the
        // whole auth flow closed with a 17pt tap target. A minHeight the
        // text is centred in survives every platform.
        style={{
          minHeight: INTERACTION.minTouchTarget,
          justifyContent: 'center',
          paddingHorizontal: SPACING.xs,
          ...webCursor,
        }}
        testID={testID}
        accessibilityRole="link"
        accessibilityLabel={accessibilityLabel}
      >
        <Text
          style={{
            fontFamily: FONTS.body,
            fontSize: 14,
            color: colors.textAccent,
          }}
        >
          {linkLabel}
        </Text>
      </Pressable>
    </Animated.View>
  )
}
